export const Brands = {
  RushGames: 'rush-games',
  BattleLeague: 'battle-league',
  VsGaming: 'vs-gaming',
  Morabaraba: 'morabaraba',
  Default: 'rush-games'
}

export const Languages = {
  English: 'en'
}

// Design handoff "Telkom Payment Pages" (13 Oct 2026): SHARED-SKELETON.md + one README per service.
// Poppins everywhere; VS Gaming sets its display lines in Barlow Condensed. Weights load in public/index.html.
const poppins = "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
const barlow = "'Barlow Condensed', 'Poppins', sans-serif"

// Shared skeleton values; each key becomes a CSS variable on the app root (joinFill -> --join-fill).
const skeleton = {
  colorText: '#ffffff',
  colorError: '#ff8a80',
  colorField: 'rgba(11, 12, 16, 0.6)',
  colorFieldBorder: 'rgba(255, 255, 255, 0.32)',
  ladderLabel: 'rgba(255, 255, 255, 0.85)',
  ladderTop: '16px',
  // The handoff's 58px/34px page padding includes the phone's status bar and home bar; in a browser the
  // safe-area insets stand in for those (App.vue).
  pageBottom: '20px',
  copyGap: '10px',
  eyebrowSize: '10px',
  eyebrowWeight: '600',
  eyebrowTracking: '.12em',
  headlineFont: poppins,
  headlineTransform: 'none',
  headlineTracking: '-.025em',
  sublineSize: '14px',
  sublineColor: 'rgba(255, 255, 255, 0.8)',
  joinHover: 'brightness(.94)',
  complianceColor: 'rgba(255, 255, 255, 0.55)',
  complianceLink: 'rgba(255, 255, 255, 0.7)',
  confirmEyebrowTracking: '.14em',
  confirmBodySize: '15px',
  confirmBodyColor: 'rgba(255, 255, 255, 0.82)',
  fontDisplay: poppins,
  fontFamily: poppins
}

const rushTheme = {
  ...skeleton,
  colorBg: '#0B0C10',
  colorPrimary: '#25C266',
  colorOnPrimary: '#062611',
  joinFill: '#25C266',
  ladderFill: '#25C266',
  ladderLabel: 'rgba(255, 255, 255, 0.92)',
  eyebrowColor: '#25C266',
  chipLead: 'rgba(37, 194, 102, 0.65)',
  chipBorder: 'rgba(37, 194, 102, 0.35)',
  // Heavier top band is deliberate: Rush art is bright.
  scrim: 'linear-gradient(180deg, rgba(11,12,16,.9) 0%, rgba(11,12,16,.58) 12%, rgba(11,12,16,.12) 27%, rgba(11,12,16,.45) 52%, rgba(11,12,16,.93) 76%, #0B0C10 100%)',
  logoHeight: '44px',
  prelanderAccent: '#25C266',
  badgeFill: '#25C266',
  badgeText: '#062611',
  confirmHeadlineSize: '38px',
  confirmHeadlineLeading: '1'
}

const battleTheme = {
  ...skeleton,
  colorBg: '#0B0C10',
  colorPrimary: '#B3F50D',
  colorOnPrimary: '#0B0C10',
  joinFill: '#B3F50D',
  ladderFill: '#B3F50D',
  eyebrowColor: '#B3F50D',
  chipLead: 'rgba(179, 245, 13, 0.65)',
  chipBorder: 'rgba(179, 245, 13, 0.35)',
  scrim: 'linear-gradient(180deg, rgba(11,12,16,.82) 0%, rgba(11,12,16,.26) 22%, rgba(11,12,16,.5) 52%, rgba(11,12,16,.94) 80%, #0B0C10 100%)',
  logoHeight: '36px',
  prelanderAccent: '#B3F50D',
  confirmHeadlineSize: '42px',
  confirmHeadlineLeading: '.98',
  confirmBodySize: '14px',
  confirmBodyColor: 'rgba(255, 255, 255, 0.78)'
}

const vsTheme = {
  ...skeleton,
  colorBg: '#05070C',
  colorPrimary: '#00C2FF',
  colorOnPrimary: '#ffffff',
  joinFill: 'linear-gradient(90deg, #0A5CFF 0%, #00C2FF 100%)',
  joinHover: 'brightness(1.08)',
  ladderFill: 'linear-gradient(90deg, #0A5CFF, #00E5FF)',
  ladderTop: '14px',
  pageBottom: '16px',
  copyGap: '0px',
  eyebrowColor: 'rgba(255, 255, 255, 0.9)',
  eyebrowSize: '11px',
  eyebrowWeight: '500',
  eyebrowTracking: '.08em',
  headlineFont: barlow,
  headlineTransform: 'uppercase',
  headlineTracking: '.01em',
  sublineSize: '15px',
  sublineColor: 'rgba(255, 255, 255, 0.85)',
  prizeColor: '#F5A623',
  chipCyan: '#00E5FF',
  complianceColor: 'rgba(255, 255, 255, 0.6)',
  complianceLink: 'rgba(255, 255, 255, 0.75)',
  scrim: 'linear-gradient(180deg, rgba(5,7,12,.7) 0%, rgba(5,7,12,.1) 22%, rgba(5,7,12,.35) 50%, rgba(5,7,12,.92) 76%, #05070C 100%)',
  logoHeight: '54px',
  prelanderAccent: '#00E5FF',
  badgeFill: 'linear-gradient(90deg, #0A5CFF, #00C2FF)',
  badgeText: '#ffffff',
  confirmHeadlineSize: '44px',
  confirmHeadlineLeading: '1',
  fontDisplay: barlow
}

// Morabaraba (WorldPlay, 5 Nov 2026): gold and ember from the crest art on a deep brown canvas; Barlow
// Condensed uppercase display lines echo the banner's "THINK. STRATEGISE." lettering.
const mrbTheme = {
  ...skeleton,
  colorBg: '#150B04',
  colorPrimary: '#F5B21B',
  colorOnPrimary: '#2B1500',
  joinFill: 'linear-gradient(90deg, #FFC43D 0%, #F08A0C 100%)',
  joinHover: 'brightness(1.06)',
  ladderFill: 'linear-gradient(90deg, #FFC43D, #F08A0C)',
  eyebrowColor: '#FFC43D',
  eyebrowSize: '11px',
  eyebrowWeight: '600',
  eyebrowTracking: '.1em',
  headlineFont: barlow,
  headlineTransform: 'uppercase',
  headlineTracking: '.01em',
  sublineSize: '15px',
  sublineColor: 'rgba(255, 255, 255, 0.85)',
  chipLead: 'rgba(255, 196, 61, 0.65)',
  chipBorder: 'rgba(255, 196, 61, 0.35)',
  complianceColor: 'rgba(255, 255, 255, 0.6)',
  complianceLink: 'rgba(255, 255, 255, 0.75)',
  // The crest sits mid-canvas under the banner and the copy: heavier in the middle than the other brands.
  scrim: 'linear-gradient(180deg, rgba(21,11,4,.9) 0%, rgba(21,11,4,.6) 16%, rgba(21,11,4,.5) 40%, rgba(21,11,4,.82) 56%, rgba(21,11,4,.95) 70%, #150B04 100%)',
  logoHeight: '60px',
  prelanderAccent: '#FFC43D',
  badgeFill: 'linear-gradient(90deg, #FFC43D, #F08A0C)',
  badgeText: '#2B1500',
  confirmHeadlineSize: '44px',
  confirmHeadlineLeading: '1',
  fontDisplay: barlow
}

// Flow copy shared by every service. Price line and compliance strip wording is fixed per service
// (WASPA Code of Conduct v17.14) and lives in each brand's translations below.
const baseTranslations = {
  ladderStep: 'Step 2 of 2',
  ladderComplete: 'Complete',

  checkingLabel: 'Checking your Telkom connection',
  joinCta: 'JOIN',

  msisdnHint: 'Enter your Telkom number and we’ll SMS you a PIN.',
  mobileLabel: 'Telkom mobile number',
  getPinCta: 'GET PIN',

  // Never the number itself: Evina whitelists exact page wordings and cannot whitelist every MSISDN.
  otpHint: 'Enter the 4-digit PIN we sent to your phone number.',
  confirmCta: 'CONFIRM',
  otpResend: 'Resend PIN',
  otpChangeNumber: 'Change number',

  // Accessible name of the close cross (cptpl_exit) in the header.
  cancelCta: 'Cancel',
  cancelledTitle: 'No problem.',
  cancelledMessage: 'Nothing has been charged. Come back any time.',
  cancelledCta: 'BACK',

  welcomeBackHint: 'Your subscription is still active, so there’s nothing to pay again today.',

  errorTitle: 'That didn’t work',
  tryAgainCta: 'TRY AGAIN',
  errorGeneric: 'Something went wrong on our side. Please try again in a moment.',
  errorNetwork: 'We couldn’t reach Telkom. Check your connection and try again.',
  errorNoService: 'Subscriptions aren’t open yet. Please check back soon.',
  invalidNumber: 'Enter a valid South African mobile number.',
  errors: {
    INVALID_MSISDN: 'That doesn’t look like a Telkom number. Check it and try again.',
    INVALID_OTP: 'That PIN isn’t right. Check the SMS and try again.',
    OPTIN_ALREADY_ACTIVE: 'This number already has an active subscription.',
    ANTI_FRAUD_BLOCK: 'We couldn’t verify this request. Please try again later from your phone on Telkom mobile data.',
    PARTNER_IS_NOT_PREMIUM: 'Subscriptions aren’t available right now. Please try again later.',
    PRODUCT_IS_NOT_ONLINE: 'This service isn’t live yet. Please check back soon.',
    EVINA_RETRY: 'Your session expired. Tap try again to start over.',
    // Our own pre-lander fraud check (functions/riskCheck.js).
    FRAUD_DETECTED: 'This visit looks automated, so we’ve stopped it here. If that’s a mistake, open the link on your phone.'
  },
  errorTitles: {
    FRAUD_DETECTED: 'Suspicious activity detected'
  },

  mockBadge: 'Mock mode',
  previewLabel: 'Preview as'
}

// Pre-lander copy (?prelander=1, design "Turn 4"): the same for every service except the two status lines
// that run under the loading bar.
const prelanderCopy = (first, second) => ({
  eyebrow: 'Loading',
  headline: ['Your game is', 'loading up.'],
  subline: 'Tap JOIN to start playing.',
  status: [first, second, 'Ready.']
})

const rushTranslations = {
  priceLine: 'Subscription service. R7.00 per day. T&Cs Apply.',
  compliance: {
    text: "Rush Games costs R7.00/day subscription service. You can cancel your subscription at any time. Full T&C's visit",
    link: 'telkom.rush-games.co.za/terms',
    help: 'Need help? Call 180'
  },
  prelander: prelanderCopy('Warming up the track…', 'Loading today’s tournament…'),
  confirmed: { style: 'badge', headline: 'You are in.', body: 'Set up your profile and start playing.', cta: 'SET UP MY PROFILE' },
  welcomeBack: { style: 'badge', headline: 'Welcome back.', cta: 'SET UP MY PROFILE' }
}

const battleTranslations = {
  priceLine: 'Subscription service. R7.00 per day. T&Cs Apply.',
  compliance: {
    text: "BLG costs R7.00/day subscription service. You can cancel your subscription at any time. Full T&C's visit",
    link: 'telkomplus.co.za/subscription-rules',
    help: 'Need help? Call 180'
  },
  prelander: prelanderCopy('Finding opponents…', 'Loading tonight’s bracket…'),
  // Headline lines and body come from the variant (each landing page has its own confirmation).
  confirmed: { style: 'split', eyebrow: 'Welcome player', cta: 'CLAIM YOUR PROFILE' },
  welcomeBack: { style: 'split', eyebrow: 'Welcome back', line1: 'Lobby', line2: 'LIVE.', cta: 'CLAIM YOUR PROFILE' }
}

const vsTranslations = {
  priceLine: 'Subscription Service. R10.00 per day. T&Cs Apply.',
  compliance: {
    text: "VS Gaming is a R10.00/day subscription service. You can cancel your subscription at any time. Full T&C's visit",
    link: 'telkomplus.co.za/subscription-rules',
    help: 'Need help? 180'
  },
  prelander: prelanderCopy('Entering the arena…', 'Loading tonight’s tournament…'),
  confirmed: { style: 'badge', headline: 'You are in.', body: 'Set up your profile and start playing.', cta: 'SET UP MY PROFILE' },
  welcomeBack: { style: 'badge', headline: 'Welcome back.', cta: 'SET UP MY PROFILE' }
}

const mrbTranslations = {
  priceLine: 'Subscription service. R7.00 per day. T&Cs Apply.',
  compliance: {
    text: "Morabaraba costs R7.00/day subscription service. You can cancel your subscription at any time. Full T&C's visit",
    link: 'telkomplus.co.za/subscription-rules',
    help: 'Need help? Call 180'
  },
  prelander: prelanderCopy('Setting the board…', 'Calling the legends…'),
  confirmed: { style: 'badge', headline: 'You are in.', body: 'Take your seat at the board and play your first game.', cta: 'PLAY NOW' },
  welcomeBack: { style: 'badge', headline: 'Welcome back.', cta: 'PLAY NOW' }
}

const msisdnRules = { countryCode: '27', trunkPrefix: '0', localLength: 9, localPrefixes: [] }

// Evina ComplianceProtect: template id fixed by Evina; landing_page_id per page is issued by Evina once
// they configure it (empty until then) and is shared by the HE and Wi-Fi/OTP flows of the same page.
const evinaTemplate = '20250825'

export const brandConfigs = {
  [Brands.RushGames]: {
    brand: Brands.RushGames,
    language: Languages.English,
    isRTL: false,

    carrier: 'Telkom',
    country: 'ZA',
    currency: 'R',
    serviceName: 'Rush Games',
    serviceTag: 'rush-games',
    extRefPrefix: 'RSH',
    // cam_Campaigns "ZTR - Web Organic - Internal" (service 122): visits that arrive without a campaign.
    organicCampaignId: 250905004,

    // sdp-enterprise: prd_Products 349 "Rush Games - Telkom - South Africa - Daily" (svc_Service 122,
    // MNOId 48). The Telkom SID is ExternalProductId.
    serviceId: 122,
    productId: 349,
    networkServiceId: '300000093',
    // R7.00 per the product owner (12 Oct 2026); prd_Products.Amount still says 5.00, flagged to the DB team.
    price: { amount: 'R7.00', period: 'day' },

    msisdn: msisdnRules,

    gameUrl: process.env.VUE_APP_RUSH_GAMES_URL || 'https://telkom.rush-games.co.za',
    termsConditionsUrl: process.env.VUE_APP_RUSH_GAMES_TERMS_URL || 'https://telkom.rush-games.co.za/terms',
    cancelUrl: '',

    assets: {
      logo: '/assets/rush-games/rush-logo@3x.png',
      logoAlt: 'Rush Games'
    },
    // Issued by Evina 15 Oct 2026, same for the HE and Wi-Fi flows; the plain link resolves to rush-01 (defaultVariant).
    evina: {
      template: evinaTemplate,
      landingPageIds: { 'rush-01': 'rush-01-20261014', 'rush-02': 'rush-02-20261014', 'rush-03': 'rush-03-20261014', 'rush-04': 'rush-04-20261014' }
    },
    // "200+ games" and "10 new every week" are service promises: keep them true or remove them.
    liveStats: {},
    defaultVariant: 'rush-01',
    // Pre-lander headline at the 390px design width (?prelander=1).
    prelander: { headlineSize: 38, headlineLeading: 1.02 },
    variants: {
      'rush-01': {
        name: 'One job',
        background: '/assets/rush-games/rush-01-bg.webp',
        eyebrow: 'Set up in two taps',
        headline: 'Beat the train.',
        headlineSize: 34,
        subline: 'Quick games. Daily leaderboards. One tap and you are in.'
      },
      'rush-02': {
        name: 'Daily tournaments',
        background: '/assets/rush-games/rush-02-bg.webp',
        eyebrow: 'Play every day',
        headline: 'Daily tournaments.',
        headlineSize: 34,
        subline: 'A fresh competition every day, entered in one tap.'
      },
      'rush-03': {
        name: 'Time to play',
        background: '/assets/rush-games/rush-03-bg.webp',
        scrim: 'linear-gradient(180deg, rgba(11,12,16,.9) 0%, rgba(11,12,16,.58) 12%, rgba(11,12,16,.14) 27%, rgba(11,12,16,.5) 46%, rgba(11,12,16,.95) 74%, #0B0C10 100%)',
        eyebrow: 'What you’re getting',
        headline: 'Time to play.',
        headlineSize: 36,
        headlineLeading: 1.02,
        subline: '200+ games. 10 new every week.',
        chips: ['200+ GAMES', '10 NEW EVERY WEEK', 'NO DOWNLOAD']
      },
      'rush-04': {
        name: 'Continuity',
        background: '/assets/rush-games/rush-04-bg.webp',
        eyebrow: 'Continue',
        headline: 'Your run is ready.',
        headlineSize: 36,
        headlineLeading: 1.02,
        subline: 'Jump straight into today’s tournament.',
        chips: ['200+ GAMES', '10 NEW EVERY WEEK', 'NO DOWNLOAD']
      }
    },

    theme: rushTheme,
    translations: { ...baseTranslations, ...rushTranslations }
  },

  [Brands.BattleLeague]: {
    brand: Brands.BattleLeague,
    language: Languages.English,
    isRTL: false,

    carrier: 'Telkom',
    country: 'ZA',
    currency: 'R',
    serviceName: 'Battle League Gaming',
    serviceTag: 'battle-league',
    extRefPrefix: 'BLG',
    // cam_Campaigns "124 - Web Organic - Internal" (service 124): visits that arrive without a campaign.
    organicCampaignId: 250909002,

    // sdp-enterprise: prd_Products 353 "Battle League Gaming - Telkom - South Africa - Daily"
    // (svc_Service 124, MNOId 48). The Telkom SID is ExternalProductId.
    serviceId: 124,
    productId: 353,
    networkServiceId: '300000090',
    price: { amount: 'R7.00', period: 'day' },

    msisdn: msisdnRules,

    gameUrl: process.env.VUE_APP_BATTLE_LEAGUE_URL || 'https://telkom.blg.gg',
    entryToken: true,
    termsConditionsUrl: process.env.VUE_APP_BATTLE_LEAGUE_TERMS_URL || 'https://telkomplus.co.za/subscription-rules',
    cancelUrl: '',

    assets: {
      logo: '/assets/battle-league/blg-logo@3x.png',
      logoAlt: 'Battle League Gaming'
    },
    // Issued by Evina 15 Oct 2026, same for the HE and Wi-Fi flows.
    evina: {
      template: evinaTemplate,
      landingPageIds: { 'blg-01': 'blg-01-20261014', 'blg-02': 'blg-02-20261014', 'blg-03': 'blg-03-20261014', 'blg-04': 'blg-04-20261014' }
    },
    // WASPA 4.1.3: counts must be bound to live data. A line or chip using an unbound {token} is not shown.
    liveStats: { players: null, titles: null },
    defaultVariant: 'blg-01',
    // Pre-lander headline at the 390px design width (?prelander=1).
    prelander: { headlineSize: 38, headlineLeading: 1.02 },
    variants: {
      'blg-01': {
        name: 'One job',
        background: '/assets/battle-league/blg-01-bg.webp',
        eyebrow: 'Set up in two taps',
        headline: 'Ready for battle?',
        headlineSize: 34,
        subline: 'Daily tournaments. Real opponents. One tap and you are in.',
        confirmed: { line1: 'Claim', line2: 'VICTORY.', body: 'You’re in. Pick your avatar, claim your tag and take your first bracket.' }
      },
      'blg-02': {
        name: 'Prove it',
        background: '/assets/battle-league/blg-02-bg.webp',
        eyebrow: 'Tonight’s bracket is open',
        headline: 'Prove it.',
        headlineSize: 36,
        headlineLeading: 1.02,
        subline: '{players} players are already in.',
        chips: ['{players} PLAYERS', 'VERIFIED LOBBIES', 'REAL PRIZES'],
        confirmed: { line1: 'You’re', line2: 'IN.', body: 'Pick your avatar, claim your tag and take tonight’s bracket.' }
      },
      'blg-03': {
        name: 'What you get',
        background: '/assets/battle-league/blg-03-bg.webp',
        scrim: 'linear-gradient(180deg, rgba(11,12,16,.82) 0%, rgba(11,12,16,.26) 20%, rgba(11,12,16,.56) 48%, rgba(11,12,16,.95) 78%, #0B0C10 100%)',
        eyebrow: 'What you’re getting',
        headline: 'Every bracket, unlocked.',
        headlineSize: 32,
        subline: 'Daily brackets, live titles, cancel anytime.',
        chips: ['DAILY BRACKETS', '{titles} TITLES LIVE', 'REAL PRIZES'],
        confirmed: { line1: 'All', line2: 'UNLOCKED.', body: 'Daily brackets and your ELO start at Trust tier New.' }
      },
      'blg-04': {
        name: 'Continuity',
        background: '/assets/battle-league/blg-04-bg.webp',
        eyebrow: 'Continue',
        headline: 'Your lobby is open.',
        headlineSize: 36,
        headlineLeading: 1.02,
        subline: 'One tap and you’re in it.',
        chips: ['{players} PLAYERS', 'VERIFIED LOBBIES', 'REAL PRIZES'],
        confirmed: { line1: 'Lobby', line2: 'LIVE.', body: 'You’re in. Pick your avatar and take your seat.' }
      }
    },

    theme: battleTheme,
    translations: { ...baseTranslations, ...battleTranslations }
  },

  [Brands.VsGaming]: {
    brand: Brands.VsGaming,
    language: Languages.English,
    isRTL: false,

    carrier: 'Telkom',
    country: 'ZA',
    currency: 'R',
    serviceName: 'VS Gaming',
    serviceTag: 'vs-gaming',
    extRefPrefix: 'VSG',
    // cam_Campaigns "ZTV - Web Organic - Internal" (service 273): visits that arrive without a campaign.
    organicCampaignId: 260902001,

    // sdp-enterprise (migration 1295 / DB577): svc_Service 273 "VS Gaming - Telkom - South Africa",
    // prd_Products 648 = Daily R10.00, ExternalProductId 300000165 (WASP partner key).
    serviceId: 273,
    productId: 648,
    networkServiceId: '300000165',
    price: { amount: 'R10.00', period: 'day' },

    msisdn: msisdnRules,

    gameUrl: process.env.VUE_APP_VS_GAMING_URL || 'https://vsgaming.co.za',
    termsConditionsUrl: process.env.VUE_APP_VS_GAMING_TERMS_URL || 'https://telkomplus.co.za/subscription-rules',
    cancelUrl: '',

    assets: {
      logo: '/assets/vs-gaming/vs-logo@3x.png',
      logoAlt: 'Telkom VS Gaming'
    },
    // Issued by Evina 15 Oct 2026, same for the HE and Wi-Fi flows (served on the Rush host with ?brand=vs-gaming).
    evina: {
      template: evinaTemplate,
      landingPageIds: { 'vs-01': 'vs-01-20261014', 'vs-02': 'vs-02-20261014', 'vs-03': 'vs-03-20261014' }
    },
    liveStats: {},
    defaultVariant: 'vs-01',
    // Pre-lander headline at the 390px design width (?prelander=1).
    prelander: { headlineSize: 44, headlineLeading: 1 },
    variants: {
      'vs-01': {
        name: 'Gems inline',
        background: '/assets/vs-gaming/vs-01-bg.webp',
        prize: { amount: '25,000', unit: 'GEMS', style: 'inline', first: true },
        eyebrow: 'Up for grabs tonight',
        headline: 'Ready for battle?',
        headlineSize: 40,
        subline: 'Daily tournaments. Real opponents. One tap and you are in.'
      },
      'vs-02': {
        name: 'Gems pill',
        background: '/assets/vs-gaming/vs-02-bg.webp',
        prize: { amount: '25,000', unit: 'GEMS', style: 'pill' },
        eyebrow: 'Up for grabs tonight',
        headline: 'Ready for battle?',
        headlineSize: 40,
        subline: 'Daily tournaments. Real opponents. One tap and you are in.'
      },
      'vs-03': {
        name: 'Claims',
        background: '/assets/vs-gaming/vs-03-bg.webp',
        eyebrow: 'Set up in two taps',
        eyebrowChip: true,
        headline: 'Ready for battle?',
        headlineSize: 44,
        subline: 'Daily tournaments. Real opponents. One tap and you are in.'
      }
    },

    theme: vsTheme,
    translations: { ...baseTranslations, ...vsTranslations }
  },

  [Brands.Morabaraba]: {
    brand: Brands.Morabaraba,
    language: Languages.English,
    isRTL: false,

    carrier: 'Telkom',
    country: 'ZA',
    currency: 'R',
    serviceName: 'Morabaraba',
    serviceTag: 'morabaraba',
    extRefPrefix: 'MRB',
    // No "Web Organic - Internal" campaign yet: product, service and campaign rows are DB-663.
    organicCampaignId: null,

    // sdp-enterprise rows are pending (DB-663), so no ServiceId / ProductId and the "already subscribed"
    // check is skipped. Telkom issued the staging SID 300000146 on 3 Nov 2026 (OPS-999, see `staging`
    // below); the production SID is still to come.
    serviceId: null,
    productId: null,
    networkServiceId: '',
    price: { amount: 'R7.00', period: 'day' },

    msisdn: msisdnRules,

    // WorldPlay's game (the return URL on Telkom's configuration form, OPS-1076); the production URL
    // is set once WorldPlay confirms it.
    gameUrl: process.env.VUE_APP_MORABARABA_URL || '',
    termsConditionsUrl: process.env.VUE_APP_MORABARABA_TERMS_URL || 'https://telkomplus.co.za/subscription-rules',
    cancelUrl: '',

    // Telkom staging (the staging-play host, or ?env=staging on any host): the staging SID and WorldPlay's
    // staging return URL (ES-691). getBrandConfig lays these over the production entries above.
    staging: {
      networkServiceId: '300000146',
      gameUrl: process.env.VUE_APP_MORABARABA_STAGING_URL || 'http://wap.zero9.co.za/mesh/prTelkomResponseStg.php?service_id=300000146'
    },

    assets: {
      logo: '/assets/morabaraba/mrb-logo@3x.png',
      logoAlt: 'Morabaraba: Legends of the Kraal'
    },
    // Not configured with Evina yet: landing_page_id stays empty until they issue one for this page.
    evina: {
      template: evinaTemplate,
      landingPageIds: { 'mrb-01': '' }
    },
    liveStats: {},
    defaultVariant: 'mrb-01',
    prelander: { headlineSize: 44, headlineLeading: 1 },
    variants: {
      'mrb-01': {
        name: 'Rule the Kraal',
        // Art (5 Nov 2026): the crest on its savanna as the full-bleed background; the banner ad above the
        // copy on JOIN (its baked-in PLAY NOW strip cropped off: JOIN is the only button on the page); the
        // crest alone on the typed-number steps.
        background: '/assets/morabaraba/mrb-01-bg.webp',
        hero: '/assets/morabaraba/mrb-01-hero.webp',
        heroPin: '/assets/morabaraba/mrb-pin-hero.webp',
        eyebrow: 'Think. Strategise.',
        headline: 'Rule the Kraal.',
        headlineSize: 44,
        subline: 'Morabaraba, the classic South African strategy game. One tap and you are in.'
      }
    },

    theme: mrbTheme,
    translations: { ...baseTranslations, ...mrbTranslations }
  }
}
